<template>
  <div class="message-box dialog-mask" @click.self="onClickOutSide">
    <div class="dialog-box">
      <h3>{{ $t('plotEditPage.createChildPlotDialog.title') }}</h3>
      <div class="section dialog-body">
        <div class="attension">
          {{ $t('plotEditPage.createChildPlotDialog.description') }}
        </div>
        <select-box
          class="selectbox"
          :items="plots"
          keyAttr="plotId"
          :placeholder="$t('plotEditPage.createChildPlotDialog.selectPlaceholder')"
          :selected="selectedPlots"
          @select="selectPlot"
        />
        <div class="input-container" v-if="selectedPlots && selectedPlots.plotId === 'new'">
          <h2>{{ $t('plotEditPage.createChildPlotDialog.selectPlotFormat') }}</h2>
          <select-box
            class="selectbox"
            :class="{ disabled: hasLimit }"
            :items="plotModes"
            keyAttr="id"
            :placeholder="$t('plotEditPage.createChildPlotDialog.selectPlaceholder')"
            @select="selectMode"
            :selected="selectedMode"
            :disabled="hasLimit"
          />
          <div v-if="hasLimit" class="caution">{{ $t('plotEditPage.createChildPlotDialog.freeMemberLimit') }}</div>
        </div>
        <div class="attension" v-if="selectedPlots && selectedPlots.plotId === 'new'">
          {{ $t('plotEditPage.createChildPlotDialog.childPlotTitleDescription') }}
        </div>
        <input-text
          class="plot-name"
          v-if="selectedPlots && selectedPlots.plotId === 'new'"
          v-model="name"
          :maxlength="120"
          :label="$t('plotEditPage.createChildPlotDialog.childPlotTitleLabel')"
        />
      </div>
      <div class="section button-area">
        <button class="button cancel" @click="onNegativeClick">{{ negative }}</button>
        <div class="border"></div>
        <button class="button primary" :disabled="!selectedPlots" @click="onPositiveClick">
          {{ positive }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import SelectBox from "@/components/molecules/SelectBox.vue";
import InputText from "@/components/atoms/InputText.vue";
import { plotModes, PlotModeSelectBoxItem, ChildPlotItem, NewChildPlotItem } from "@/lib/plot";
import { SubPlot } from "@/lib/models";

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { SelectBox, InputText },
  props: {
    negative: {
      type: String,
      default() {
        return this.$t('common.cancel');
      },
    },
    positive: {
      type: String,
      default() {
        return this.$t('plotEditPage.createChildPlotDialog.createButton');
      },
    },
    plotList: {
      type: Array as PropType<SubPlot[]>,
    },
    hasLimit: Boolean,
    negativeCallback: Function,
    positiveCallback: Function,
  },
  data() {
    return {
      selectedPlots: undefined,
      plots: [],
      name: "",
      selectedMode: plotModes[0],
      plotModes,
    };
  },
  created() {
    const defaultPlots = [
      {
        plotId: "new",
        name: this.$t('plotEditPage.createChildPlotDialog.newCreate') as string,
        image: "none",
      },
    ];
    this.plots = defaultPlots.concat(this.plotList.map((i) => ({ plotId: i.plotId, name: i.name, image: "none" })));
  },
  methods: {
    onNegativeClick() {
      if (this.negativeCallback) {
        this.negativeCallback();
      }

      this.$close(true);
    },
    onPositiveClick() {
      if (this.positiveCallback) {
        const data = {
          plotId: this.selectedPlots!.plotId,
          name: this.selectedPlots!.name,
          mode: this.selectedMode,
        };
        if (data.plotId === "new") {
          data.name = this.name || this.$t('plotEditPage.createChildPlotDialog.plotNameUnset') as string;
        }
        this.positiveCallback(data as NewChildPlotItem);
      }

      this.$close(true);
    },
    onClickOutSide() {
      this.$close(true);
    },
    selectPlot(plots) {
      this.selectedPlots = plots;
    },
    selectMode(item) {
      this.selectedMode = item;
    },
  },
});

interface Props {
  negative?: string;
  positive?: string;
  plotList: Array<SubPlot>;
  hasLimit: Boolean;
  negativeCallback?: () => void;
  positiveCallback?: (data: NewChildPlotItem) => void;
}

interface Data {
  selectedPlots?: SubPlot;
  plots: Array<ChildPlotItem>;
  name: string;
  selectedMode: PlotModeSelectBoxItem;
  plotModes: PlotModeSelectBoxItem[];
}

interface Computed {}

interface Methods {
  onNegativeClick: () => void;
  onPositiveClick: () => void;
  onClickOutSide: () => void;
  selectPlot: (plot: SubPlot) => void;
  selectMode(item: PlotModeSelectBoxItem): void;
}

export type CreateChildPlotDialogProps = Props;
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.fade-enter-active {
  transition: opacity 0.3s;
}

.fade-leave-active {
  transition: opacity 0.1s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.dialog-mask {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: -ms-flexbox;
  display: flex;
  z-index: 9000;
  -ms-flex-align: center;
  align-items: center;
  -ms-flex-pack: center;
  justify-content: center;
  background-color: var(--color-overlay);
}

.dialog-box {
  width: 360px;
  margin: 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background-color: var(--color-background-normal);
  box-shadow: var(--shadow-md);
  color: var(--color-text-normal);

  .section {
    border-bottom: 2px solid var(--color-border);
    box-sizing: border-box;
    padding: 20px;
  }
  h3 {
    text-align: center;
    margin-top: 20px;
  }

  .dialog-body {
    font-weight: var(--font-weight-bold);
    background-color: var(--color-surface-subtle);

    .attension {
      text-align: center;
    }
    .plot-name {
      margin-top: 20px;
      width: 100%;
    }

    .input-container {
      margin-top: 1em;

      h2 {
        margin: 0;
        margin-bottom: 10px;
        font-size: 0.8em;
        font-weight: var(--font-weight-regular);
      }

      .caution {
        color: var(--color-text-low);
        font-size: 0.75em;
        margin-top: 10px;
      }
    }
  }

  .button-area {
    border-bottom: none;
    display: flex;
    justify-content: center;
    padding: 0;

    .border {
      width: 2px;
      background-color: var(--color-border);
    }

    .button {
      position: relative;
      width: 50%;
      margin: 5px 0;
      font-size: 1.1em;
      box-sizing: border-box;
      border-radius: var(--radius-xs);
      font-weight: var(--font-weight-bold);
      cursor: pointer;
      @include hover-overlay;

      &:disabled {
        &::after {
          display: none;
        }
      }

      &.cancel {
        background-color: transparent;
        color: var(--color-text-low);
      }
      &.primary {
        background-color: transparent;
        color: var(--color-primary);
        border: none;
        &:disabled {
          color: var(--color-inactive);
        }
      }
    }
  }
}
.selectbox {
  margin-top: 10px;
  margin-bottom: 10px;
  background-color: var(--color-background-normal);

  ::v-deep .icon-wrapper {
    display: none;
  }
}
</style>
